import { forwardRef, useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./B1ShipDetails.scss";

import { AbsDropDown, Button, Input, Label, TextArea } from "abs-ui-components";

import { preventScientificNotation } from "../../../../../services/utilities";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { RadioGroup, FormControlLabel, Radio, Autocomplete, TextField } from "@mui/material";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { AUTOSAVE_SUCCESS_MESSAGE, createB1Payload, REQUIRED_FIELDS_FOR_B1 } from "../../Constants";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const B1ShipDetails = forwardRef(({ B1ShipDetailsData, B1ShipDropdownValues, vesselMstId, refetchMonitoringData }, ref) => {
	const {
		control,
		formState: { errors, isDirty },
		trigger,
		watch,
		getValues,
		setValue,
		reset,
	} = useFormContext();

	const [dropdownOptions, setDropdownOptions] = useState({
		countryOwnerOptions: [],
		iceClassOptions: [],
		countryFlagOptions: [],
		technicalEfficiencyOptions: [],
		classificationSocietyOptions: [],
		shipTypes: [],
	});
	const [isEfficiencyValueRequired, setIsEfficiencyValueRequired] = useState();
	const [subTypeOptions, setSubTypeOptions] = useState([]);
	const [isSubTypeRequired, setIsSubTypeRequired] = useState();
	const manualSaveTriggered = useRef(false);

	useEffect(() => {
		if (!B1ShipDropdownValues || !dropdownOptions._childrenByParent) return;
		const parent = B1ShipDropdownValues.shipTypes.find((t) => t.code === B1ShipDetailsData?.shipType && t.parentMrvVesselTypeId === null);
		const kids = dropdownOptions._childrenByParent?.[parent?.mrvVesselTypeMstId] ?? [];
		setSubTypeOptions(kids);
		setIsSubTypeRequired(kids.length > 0);
	}, [B1ShipDetailsData, B1ShipDropdownValues, dropdownOptions._childrenByParent]);

	const selectedTechnicalEfficiency = watch("technicalEfficiency");
	const isTextDisabled = selectedTechnicalEfficiency?.value === "Not Applicable";

	useEffect(() => {
		setIsEfficiencyValueRequired(
			B1ShipDetailsData &&
				B1ShipDetailsData?.technicalEfficiency !== "" &&
				!["na", "not applicable"].includes(B1ShipDetailsData?.technicalEfficiency?.toLowerCase())
		);
	}, [B1ShipDetailsData]);

	// Set dynamic md value
	const techEffMd = isEfficiencyValueRequired ? "2" : "4";

	const isIceClassYes = watch("isIceClassShip");
	const iceClassMd = isIceClassYes === true ? "2" : "4";

	useEffect(() => {
		if (!B1ShipDropdownValues) return;
		const { parentOptions, childrenByParentId } = buildShipTypeOptions(B1ShipDropdownValues.shipTypes);
		const mapOptions = (list, labelKey, valueKey) =>
			(list ?? [])
				.filter((item) => item?.[labelKey] && item?.[valueKey])
				.map((item) => ({
					label: item[labelKey],
					value: item[valueKey],
				}));

		const dropdownMap = [
			{
				apiKey: "countryCodes",
				stateKey: "countryOwnerOptions",
				labelKey: "countryName",
				valueKey: "countryCode",
			},
			{
				apiKey: "iceClassPolarCodes",
				stateKey: "iceClassOptions",
				labelKey: "value",
				valueKey: "code",
			},
			{
				apiKey: "countryCodes",
				stateKey: "countryFlagOptions",
				labelKey: "countryName",
				valueKey: "countryCode",
			},
			{
				apiKey: "technicalEfficiencyCodes",
				stateKey: "technicalEfficiencyOptions",
				labelKey: "value",
				valueKey: "code",
			},
			{
				apiKey: "classificationSocieties",
				stateKey: "classificationSocietyOptions",
				labelKey: "name",
				valueKey: "code",
			},

			{ apiKey: "shipTypes", stateKey: "shipTypes", labelKey: "value", valueKey: "code" },
		];

		const formatted = dropdownMap.reduce((acc, cfg) => {
			if (cfg.apiKey === "shipTypes") {
				acc.shipTypes = parentOptions;
				acc._childrenByParent = childrenByParentId;
			} else {
				acc[cfg.stateKey] = mapOptions(B1ShipDropdownValues?.[cfg.apiKey], cfg.labelKey, cfg.valueKey);
			}
			return acc;
		}, {});
		setDropdownOptions((prev) => ({ ...prev, ...formatted }));
	}, [B1ShipDropdownValues]);

	useEffect(() => {
		if (B1ShipDetailsData) {
			reset({
				shipName: B1ShipDetailsData?.shipName || "",
				imoNumber: B1ShipDetailsData?.imoNumber || "",
				portOfRegistry: B1ShipDetailsData?.portOfRegistry || "",
				homePort: B1ShipDetailsData?.homePort || "",
				shipOwnerName: B1ShipDetailsData?.shipOwnerName || "",
				shipOwnerTaxNumber: B1ShipDetailsData?.shipOwnerTaxNumber || "",
				shipOwnerContactPerson: B1ShipDetailsData?.shipOwnerContactPerson || "",
				businessAddress: B1ShipDetailsData?.businessAddress || "",
				city: B1ShipDetailsData?.city || "",
				stateProvinceRegion: B1ShipDetailsData?.stateProvinceRegion || "",
				businessTelephone: B1ShipDetailsData?.businessTelephone || "",
				businessEmail: B1ShipDetailsData?.businessEmail || "",
				registeredOwnerIMO: B1ShipDetailsData?.registeredOwnerIMO || "",
				deadweight: B1ShipDetailsData?.deadweight === 0 ? null : B1ShipDetailsData?.deadweight ?? null,
				grossTonnage: Math.round(B1ShipDetailsData?.GrossTonnage ?? B1ShipDetailsData?.grossTonnage),
				registeredOwnerCountry: B1ShipDetailsData?.registeredOwnerCountry || "",
				classificationSociety: B1ShipDetailsData?.classificationSociety || "",
				flag: B1ShipDetailsData?.flag || "",
				shipType: B1ShipDetailsData?.shipType || "",
				shipSubType: B1ShipDetailsData?.shipSubType || "",
				technicalEfficiency: B1ShipDetailsData?.technicalEfficiency || "",
				technicalEfficiencyValue: B1ShipDetailsData?.technicalEfficiencyValue === 0 ? null : B1ShipDetailsData?.technicalEfficiencyValue ?? null,
				isIceClassShip: B1ShipDetailsData?.isIceClassShip || false,
				iceClass: B1ShipDetailsData?.iceClass || "",
				additionalInformation: B1ShipDetailsData?.additionalInformation || "",
				businessPrinciplePlace: B1ShipDetailsData?.businessPrinciplePlace || "",
			});
		}
	}, [B1ShipDetailsData, setValue]);

	const buildShipTypeOptions = (list = []) => {
		const parents = list.filter((t) => t.parentMrvVesselTypeId === null);
		return {
			parentOptions: parents.map((t) => ({
				label: t.value,
				value: t.code,
				id: t.mrvVesselTypeMstId,
			})),
			childrenByParentId: list.reduce((acc, t) => {
				if (t.parentMrvVesselTypeId !== null) {
					(acc[t.parentMrvVesselTypeId] ??= []).push({
						label: t.value,
						value: t.code,
					});
				}
				return acc;
			}, {}),
		};
	};

	useEffect(() => {
		if (isTextDisabled) {
			setValue("technicalEfficiencyText", "");
		}
	}, [isTextDisabled, setValue]);

	const handleBlur = async (field) => {
		const isValid = await trigger(field);
		if (!isValid) return;
	};

	const handleImoNumberBlur = async (field, value) => {
		await trigger(field);
	};

	//Autosave call
	AutoSave(ref, REQUIRED_FIELDS_FOR_B1, async () => {
		if (!isDirty) return;
		const formValues = getValues();
		await onSubmit(formValues, true);
	});

	const onSubmit = async (data, isAuto = false) => {
		const payload = createB1Payload(data);
		try {
			const response = await callApi({ url: API_ROUTES.B1(vesselMstId), method: "POST", data: payload });
			if (response?.isSuccess) {
				emitToasterMsg("success", "Table B.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
				reset(getValues());
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table B.1", response?.errorMessage || "Save failed");
			}
		} catch (error) {
			emitToasterMsg("error", "Table B.1", error?.errorMessage);
		}
	};

	return (
		<>
			<div className="form-section mt-5">
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Name of the ship" bold required />
						<Controller
							name="shipName"
							control={control}
							rules={{
								required: "Field is required",
							}}
							render={({ field }) => (
								<>
									<Input
										id="name_of_the_ship"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipName")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.shipName?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="IMO ship identification number" bold required />
						<Controller
							name="imoNumber"
							control={control}
							rules={{
								required: "Please provide a 7 digit IMO number",
								validate: (value) => value?.length === 7 || "IMO number must be exactly 7 characters",
							}}
							render={({ field }) => (
								<>
									<Input
										id="vessel_imo_number"
										type="number"
										autoFocus={false}
										value={field?.value}
										onChange={(e) => field.onChange(e)}
										className="input-height"
										min="1"
										minLength={7}
										maxLength={7}
										onBlur={(e) => handleImoNumberBlur(field.name, field.value)}
										onWheel={(e) => e.target.blur()}
										onKeyDown={preventScientificNotation}
										disabled
										onInput={(e) => {
											if (e.target.value?.length > 7) {
												e.target.value = e.target.value.slice(0, 7);
											}
										}}
									/>
									<div className="error-msg-b1ship">{errors?.imoNumber?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Port of registry" bold />
						<Controller
							name="portOfRegistry"
							control={control}
							render={({ field }) => (
								<>
									<Input
										id="port_of_registry"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("portOfRegistry")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.portOfRegistry?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Home port (if not identical with port of registry)" bold />
						<Controller
							name="homePort"
							render={({ field }) => (
								<>
									<Input
										id="home_port"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("homePort")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.homePort?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Name of the shipowner" bold required />
						<Controller
							name="shipOwnerName"
							control={control}
							rules={{
								required: "Field is required",
							}}
							render={({ field }) => (
								<>
									<Input
										id="ship_owner_name"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerName")}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerName?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Shipowner's tax number(voluntary)" bold />
						<Controller
							name="shipOwnerTaxNumber"
							control={control}
							render={({ field }) => (
								<>
									<Input
										id="shipowner_tax_number"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerTaxNumber")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerTaxNumber?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Contact person for the shipowner (voluntary)" bold />
						<Controller
							name="shipOwnerContactPerson"
							control={control}
							render={({ field }) => (
								<>
									<Input
										id="contact_person"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("shipOwnerContactPerson")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.shipOwnerContactPerson?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Business address" bold required />
						<Controller
							name="businessAddress"
							control={control}
							rules={{
								required: "Field is required",
							}}
							render={({ field }) => (
								<>
									<Input
										id="business_address"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessAddress")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.businessAddress?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="City (voluntary)" bold />
						<Controller
							name="city"
							control={control}
							render={({ field }) => (
								<>
									<Input
										id="city"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("city")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.city?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="State/Province/Region (voluntary)" bold />
						<Controller
							name="stateProvinceRegion"
							control={control}
							render={({ field }) => (
								<>
									<Input
										id="state"
										autoFocus={false}
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("stateProvinceRegion")}
										maxLength={255}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.stateProvinceRegion?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Telephone number (voluntary)" bold />
						<Controller
							name="businessTelephone"
							control={control}
							defaultValue={B1ShipDetailsData?.businessTelephone ?? ""}
							rules={{
								validate: (value) => {
									if (!value) return true;
									return /^\+?[0-9]{7,15}$/.test(value) ? true : "Please enter a valid phone number.";
								},
							}}
							render={({ field }) => (
								<>
									<Input
										id="telephone_number"
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessTelephone")}
										maxLength={16}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.businessTelephone?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="4">
						<Label name="Email address" bold />
						<Controller
							name="businessEmail"
							control={control}
							defaultValue={B1ShipDetailsData?.businessEmail ?? ""}
							rules={{
								validate: (value) => {
									if (!value) return true; 
									return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value) ? true : "Please enter a valid email address.";
								},
							}}
							render={({ field }) => (
								<>
									<Input
										id="email_address"
										value={field.value}
										onChange={field.onChange}
										onBlur={() => handleBlur("businessEmail")}
										maxLength={254} 
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.businessEmail?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="IMO unique company and registered owner identification number of the registered owner" bold />
						<Controller
							name="registeredOwnerIMO"
							control={control}
							render={({ field }) => (
								<Input
									autoFocus={false}
									id="imo_unique_companu_number"
									className="form-input"
									value={field.value}
									onChange={field.onChange}
									onBlur={() => handleBlur("registeredOwnerIMO")}
								/>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Deadweight (in metric tonnes)" bold />
						<Controller
							name="deadweight"
							control={control}
							rules={{
								validate: (value) => /^\d{1,15}(\.\d{0,2})?$/.test(value) || "Please enter a value with size betwen 0 and 15.",
							}}
							render={({ field }) => (
								<>
									<Input
										id="dead_weight"
										type="number"
										autoFocus={false}
										value={field.value === 0 ? "" : field.value ?? ""}
										onChange={(val) => {
											const parsed = val === "" ? null : val;
											field.onChange(parsed);
										}}
										onBlur={() => {
											const numericValue = parseFloat(field.value);
											if (!isNaN(numericValue)) {
												field.onChange(numericValue.toFixed(2));
											}
											handleBlur("deadweight");
										}}
										className="form-input"
										maxLength={18}
									/>
									<div className="error-msg-b1ship">{errors?.deadweight?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Gross Tonnage" bold />
						<Controller
							name="grossTonnage"
							control={control}
							rules={{
								validate: (value) => !value || String(value)?.length <= 9 || "Please enter a number with max 9 digits",
							}}
							render={({ field }) => (
								<>
									<Input
										id="gross_tonnage"
										type="number"
										autoFocus={false}
										value={field.value === 0 ? "" : field.value ?? ""}
										onChange={(val) => {
											const parsed = val === "" ? null : val;
											field.onChange(parsed);
										}}
										onInput={(e) => {
											e.target.value = e.target.value.replace(/\D/g, "");
											if (e.target.value.length > 9) {
												e.target.value = e.target.value.slice(0, 9);
											}
										}}
										onBlur={() => handleBlur("grossTonnage")}
										className="form-input"
									/>
									<div className="error-msg-b1ship">{errors?.grossTonnage?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md="4">
						<Label name="Country of registration of the registered owner" bold required />
						<Controller
							name="registeredOwnerCountry"
							control={control}
							rules={{
								required: "Field is required",
							}}
							render={({ field }) => (
								<>
									<Autocomplete
										id="registered_owner_country"
										options={dropdownOptions.countryOwnerOptions}
										getOptionLabel={(option) => option.label}
										isOptionEqualToValue={(option, value) => option.value === value}
										value={dropdownOptions.countryOwnerOptions.find((opt) => opt.value === field.value) || null}
										onChange={(_, selected) => field.onChange(selected?.value || "")}
										onBlur={() => handleBlur("registeredOwnerCountry")}
										renderInput={(params) => (
											<TextField
												{...params}
												fullWidth
												variant="outlined"
												size="small"
												InputProps={{
													...params.InputProps,
													className: "autocomplete-input",
												}}
											/>
										)}
									/>
									<div className="error-msg-b1ship">{errors?.registeredOwnerCountry?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Classification Society (voluntary)" bold />
						<Controller
							name="classificationSociety"
							control={control}
							render={({ field }) => {
								const selectedOption = dropdownOptions.classificationSocietyOptions.find((opt) => opt.value === field.value) || null;
								return (
									<>
										<Autocomplete
											id="classification_society"
											options={dropdownOptions.classificationSocietyOptions}
											getOptionLabel={(option) => option.label || ""}
											isOptionEqualToValue={(o, v) => o?.value === v?.value}
											value={selectedOption}
											onChange={(_, selected) => field.onChange(selected?.value || "")}
											onBlur={() => handleBlur(field.name)}
											renderInput={(params) => (
												<TextField
													{...params}
													fullWidth
													variant="outlined"
													size="small"
													InputProps={{
														...params.InputProps,
														className: "autocomplete-input",
													}}
												/>
											)}
										/>
										<div className="error-msg-b1ship">{errors?.classificationSociety?.message || <>&nbsp;</>}</div>
									</>
								);
							}}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Flag" bold required />
						<Controller
							name="flag"
							control={control}
							rules={{
								required: "Field is required",
							}}
							render={({ field }) => {
								const selectedOption = dropdownOptions.countryFlagOptions.find((opt) => opt.value === field.value) || null;
								return (
									<>
										<Autocomplete
											id="flag"
											options={dropdownOptions.countryFlagOptions}
											getOptionLabel={(option) => option.label || ""}
											isOptionEqualToValue={(option, value) => option.value === value?.value}
											value={selectedOption}
											onChange={(_, selected) => field.onChange(selected?.value || "")}
											onBlur={() => handleBlur(field.name)}
											renderInput={(params) => (
												<TextField
													{...params}
													placeholder="Search Country"
													fullWidth
													variant="outlined"
													size="small"
													InputProps={{
														...params.InputProps,
														className: "autocomplete-input",
													}}
												/>
											)}
										/>
										<div className="error-msg-b1ship">{errors?.flag?.message || <>&nbsp;</>}</div>
									</>
								);
							}}
						/>
					</MDBCol>
				</MDBRow>
				<MDBRow className="align-items-center mb-2">
					<MDBCol md={isSubTypeRequired ? "2" : "4"}>
						<Label name="Type of the ship" required bold />
						<Controller
							name="shipType"
							control={control}
							rules={{
								required: "Field is required",
								validate: (value) => {
									const isValidOption = dropdownOptions.shipTypes?.some((option) => option.value === value);
									return isValidOption || "Field is required";
								},
							}}
							render={({ field }) => (
								<>
									<AbsDropDown
										id="type_of_ship"
										options={dropdownOptions.shipTypes}
										selected={field.value}
										className="compact-dropdown"
										handleChange={(selected) => {
											field.onChange(selected?.value);
											const parent = B1ShipDropdownValues.shipTypes.find(
												(t) => t.code === selected?.value && t.parentMrvVesselTypeId === null
											);
											const kids = dropdownOptions._childrenByParent?.[parent?.mrvVesselTypeMstId] ?? [];

											setSubTypeOptions(kids);
											setIsSubTypeRequired(kids.length > 0);
										}}
										onBlur={() => handleBlur(field.name)}
										placeholder="Select Type of the ship"
									/>
									<div className="error-msg-b1ship">{errors?.shipType?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{isSubTypeRequired && (
						<MDBCol md="2">
							<Label name="Sub Ship Type" bold />
							<Controller
								name="shipSubType"
								control={control}
								rules={{
									validate: (value) => {
										if (isSubTypeRequired) {
											return value ? true : "Field is required";
										}
										return true;
									},
								}}
								render={({ field }) => (
									<>
										<AbsDropDown
											options={subTypeOptions}
											selected={field.value}
											className="compact-dropdown"
											handleChange={(selected) => field.onChange(selected?.value)}
											placeholder="Select Sub Ship Type"
											isClearable
											onBlur={() => handleBlur(field.name)}
										/>
										<div className="error-msg-b1ship">{errors?.shipSubType?.message || <>&nbsp;</>}</div>
									</>
								)}
							/>
						</MDBCol>
					)}
					<MDBCol md={techEffMd}>
						<Label name="Technical Efficiency (voluntary)" bold />
						<Controller
							name="technicalEfficiency"
							control={control}
							render={({ field }) => (
								<>
									<AbsDropDown
										options={dropdownOptions.technicalEfficiencyOptions}
										selected={field.value}
										handleChange={(selected) => {
											field.onChange(selected.value);
											setIsEfficiencyValueRequired(!["na", "not applicable"].includes(selected.value?.toLowerCase()));
										}}
										placeholder="Select Technical Efficiency"
										onBlur={() => handleBlur(field.name)}
										isClearable
									/>
									<div className="error-msg-technicalEfficiency">{"" || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{isEfficiencyValueRequired && (
						<MDBCol md="2">
							<Label name="Technical Efficiency Value" bold />
							<Controller
								name="technicalEfficiencyValue"
								control={control}
								render={({ field }) => (
									<>
										<Input
											{...field}
											placeholder="Technical Efficiency Value"
											autoFocus={false}
											value={field.value === 0 ? "" : field.value ?? ""}
											onChange={(val) => {
												const parsed = val === "" ? null : val;
												field.onChange(parsed);
											}}
											className="compact-input"
										/>
										<div className="error-msg-b1ship">{"" || <>&nbsp;</>}</div>
									</>
								)}
							/>
						</MDBCol>
					)}
					<MDBCol md={iceClassMd}>
						<Label name="Ice Class" required bold />
						<Controller
							name="isIceClassShip"
							control={control}
							rules={{
								validate: (value) => (value === true || value === false ? true : "Field is required"),
							}}
							render={({ field }) => (
								<>
									<RadioGroup
										row
										value={field.value === true ? "true" : field.value === false ? "false" : ""}
										onChange={(e) => {
											const val = e.target.value === "true";
											field.onChange(val);
											if (!val) {
												setValue("iceClass", null);
											}
										}}
									>
										<FormControlLabel
											value="true"
											control={
												<Radio
													sx={{
														color: "#032A4F",
														"&.Mui-checked": { color: "#032A4F" },
													}}
												/>
											}
											label="Yes"
										/>
										<FormControlLabel
											value="false"
											control={
												<Radio
													sx={{
														color: "#032A4F",
														"&.Mui-checked": { color: "#032A4F" },
													}}
												/>
											}
											label="No"
										/>
									</RadioGroup>
									<div className="error-msg-b1ship">{errors?.isIceClassShip?.message || <>&nbsp;</>}</div>
								</>
							)}
						/>
					</MDBCol>
					{isIceClassYes === true && (
						<MDBCol md="2">
							<Label name="Select Ice Class" required bold />
							<Controller
								name="iceClass"
								control={control}
								rules={{
									validate: (value) => {
										if (isIceClassYes) {
											if (!value) return "Field is required";
											const isValidOption = dropdownOptions?.iceClassOptions?.some((opt) => opt.value === value);
											return isValidOption || "Field is required";
										}
										return true;
									},
								}}
								render={({ field }) => (
									<>
										<AbsDropDown
											options={dropdownOptions?.iceClassOptions}
											selected={field?.value}
											handleChange={(selected) => field.onChange(selected?.value)}
											placeholder="Select Ice Class"
											isClearable
											disabled={!isIceClassYes}
										/>
										<div className="error-msg-b1ship">{errors?.iceClass?.message || <>&nbsp;</>}</div>
									</>
								)}
							/>
						</MDBCol>
					)}
				</MDBRow>
				<MDBRow className="align-items-start mb-2">
					<MDBCol md="4">
						<Label name="Voluntary open description field for additional information about the characteristics of the ship" bold />
						<Controller
							name="additionalInformation"
							control={control}
							render={({ field }) => (
								<TextArea
									id="additional_information"
									className="textarea-resize"
									value={field?.value || ""}
									onChange={field.onChange}
									maxLength={1000}
									rows={1}
								/>
							)}
						/>
					</MDBCol>
					<MDBCol md="4">
						<Label name="Principle place of business" bold />
						<Controller
							name="businessPrinciplePlace"
							control={control}
							render={({ field }) => (
								<TextArea
									id="principle_business"
									className="textarea-resize"
									value={field?.value || ""}
									onChange={field.onChange}
									maxLength={512}
									rows={1}
								/>
							)}
						/>
					</MDBCol>
				</MDBRow>
				<div className="form-row" style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
					<Button
						disabled={!isDirty}
						onClick={async () => {
							const isValid = await trigger([...REQUIRED_FIELDS_FOR_B1, "businessTelephone", "businessEmail"]);
							if (!isValid) return;

							manualSaveTriggered.current = true;
							await onSubmit(getValues(), false);
						}}
					>
						Save
					</Button>
				</div>
			</div>
		</>
	);
});

export default B1ShipDetails;
